export const CLIENT_CARE_POLICY = {
    title: 'Client Care Policy',
    type: 'CLIENT_CARE_POLICY',
    version: 5,
    effective_date: 'Dec 27th, 2025',
    markup: `<section class="bck-client-care-policy">
    <h1>BCK Client Care Policy</h1>
  
    <p>
      This Client Care Policy is provided for informational purposes only and is not intended to create
      contractual obligations or guarantees. In the event of any conflict, BCK’s Terms &amp; Conditions
      and active service agreements will govern.
    </p>
  
    <h2>Introduction</h2>
    <p>
      BCK supports small businesses by providing clear, dependable care for their online presence. Our
      services focus on helping you establish, maintain, and optimize your website with confidence.
    </p>
    <p>
      We provide website hosting and monitoring, web development and design, and ongoing website
      support. Our work is built around long-term web presence management designed specifically for
      small businesses.
    </p>
    <p>
      Our goal is simple: your website should feel safe, manageable, and clearly supported, so you can
      focus on running your business while we handle the technical side.
    </p>
    <p>
      This policy is intended for clients who value clear communication, realistic expectations, and a
      managed approach to their web presence.
    </p>
  
    <h2>How We Support Your Small Business</h2>
    <p>
      We work with small businesses that have different levels of need when it comes to their online
      presence. Some clients want minimal involvement, while others prefer hands-on, ongoing care.
    </p>
    <p>
      You have the freedom to choose how much support you need. You can start with a lighter level of
      care and adjust over time as your business grows. The hosting and support level you select
      directly reflects the level of monitoring, responsiveness, and services we provide.
    </p>
    <p>
      Lighter support levels typically focus on core hosting, monitoring, and basic assistance. Higher
      levels of support include more proactive involvement, planning, and hands-on care. This
      structure allows you to align services with your business needs at any given time.
    </p>
    <p>
      With years of experience in web presence management, web development, and design, we take time
      to explain your options clearly. Our role is to make sure you understand what each level of
      support includes so you can make informed decisions without pressure.
    </p>
  
    <h2>Communication and Responsiveness</h2>
    <p>
      We prioritize clear, timely, and direct communication. We understand that questions about your
      website can be time-sensitive, and we aim to be available and responsive.
    </p>
  
    <p><strong>Key support details:</strong></p>
    <ul>
      <li><strong>Business hours:</strong> Monday through Friday, 9:00 AM to 6:00 PM Central Standard Time</li>
      <li><strong>Response goal:</strong> Within 1 to 2 business days (during business hours)</li>
      <li>
        <strong>Urgent issues:</strong> Issues that impact site availability, security, or critical business
        functionality. You may contact your BCK Web Admin directly using the contact information
        provided during onboarding. We will return urgent inquiries as soon as possible.
      </li>
    </ul>
  
    <p>
      Clients have direct access to their assigned BCK Web Admin using the contact information
      provided during onboarding. Holidays and planned office closures may impact response times, and
      we make every effort to communicate closures in advance. Emergency monitoring continues based
      on your service plan.
    </p>
  
    <h2>Trust and Safety</h2>
    <p>
      Trust and online safety are central to how we work. Any business information you share with
      your BCK Web Admin or with BCK is protected and treated with care.
    </p>
    <p>
      Client data is never sold and is not shared outside of our trusted vendor network unless
      required to deliver services. Access to sensitive credentials is limited to what is necessary to
      support your account.
    </p>
    <p>
      If you ever have questions about how your data is protected or how we can better support your
      security needs, we encourage you to reach out. Transparency is a core part of our care model.
    </p>

    <h2>Active Plan Requirement for Ongoing Support</h2>

    <p>
      To provide consistent, reliable care, an active BCK plan is required for ongoing hosting and support
      services.
    </p>
  
    <p>
      When you are a client of BCK, you are enrolled in a hosting and support plan that aligns with your
      small business’s needs. This plan determines the level of care, monitoring, responsiveness, and
      services we provide for your website and related online assets.
    </p>
  
    <p>
      Ongoing support, hosting, monitoring, and access to your BCK Web Admin are available only while an
      active plan is in place.
    </p>
  
    <p>
      If you decide to discontinue your plan with BCK, we ask for <strong>15 days’ notice</strong>. This
      notice period allows time to properly plan for the transfer of any websites, data, or digital assets
      back to you, and to ensure a smooth and orderly transition.
    </p>
  
    <p><strong>Once a plan has ended:</strong></p>
    <ul>
      <li>Hosting services provided by BCK will be discontinued</li>
      <li>Ongoing support and monitoring will end</li>
      <li>Access to managed services associated with the plan will no longer be available</li>
    </ul>
  
    <p>
      We are always happy to assist with planning transitions or changes in advance. Our goal is to ensure
      clarity, protect your assets, and avoid disruption, whether you continue working with BCK or decide
      to move in a different direction.
    </p>
  
    <h2>Uptime Assurance</h2>
    <p>
      Our role includes helping ensure your website and online assets continue to operate day and
      night.
    </p>
  
    <p><strong>Uptime and monitoring:</strong></p>
    <ul>
      <li><strong>Monitoring coverage:</strong> 24/7/365 monitoring for downtime and critical issues</li>
    </ul>
  
    <p>
      When a problem is detected, automated alerts allow us to investigate and respond based on your
      service level.
    </p>
    <p>
      While no hosting environment can guarantee uninterrupted availability, we take proactive steps
      to reduce downtime and address issues as quickly as possible within scope.
    </p>
  
    <h2>Meetings and Using Time Efficiently</h2>
    <p>
      Meetings and check-ins are designed to be productive working sessions that respect everyone’s
      time.
    </p>
  
    <p><strong>Meeting basics:</strong></p>
    <ul>
      <li><strong>Standard meeting length:</strong> 50 minutes (unless otherwise discussed in advance)</li>
      <li><strong>Meeting format:</strong> Virtual by default</li>
      <li><strong>Meeting tool:</strong> Google Meet (no downloads required, join from your web browser)</li>
    </ul>
  
    <p>
      Check-ins may be used to review website performance, plan updates or improvements, discuss
      questions or concerns, and outline next steps. Clients are encouraged to come prepared so
      meeting time can be used effectively.
    </p>
    <p>
      When additional time is needed beyond scheduled meetings or included support, we communicate
      clearly. Scope and next steps are outlined in advance, and no work proceeds without
      transparency.
    </p>
  
    <h2>Our Commitment to Partnership</h2>
    <p>
      BCK views client relationships as long-term partnerships. We aim to be a steady, reliable
      presence as your business grows and changes.
    </p>
    <p>
      Our commitment is to provide honest guidance, respectful communication, and thoughtful care for
      your online presence. When questions come up or circumstances change, we work with you to find
      clear, reasonable solutions that support your goals.
    </p>
  
    <p><em>Last updated: December 2025</em></p>
  </section>
  
    `
}